import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {authExam} from '../../auth/core/_requests'
import {useAuth} from '../../auth/core/Auth'
import React, {useCallback, useState} from 'react'
import {InputGroup} from 'react-bootstrap'
import {Link, useNavigate} from 'react-router-dom'
import {GoogleReCaptcha, GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'
import {intervalRefreshRecaptcha} from '../../../../_metronic/helpers'
import LoadingButtonWrapper from '../../../components/LoadingButtonWrapper'
import {parseJsonAndPascalToCamel} from '../../../utils/array'

const loginSchema = Yup.object().shape({
  username: Yup.string().min(3).max(50).required('NIM wajib diisi'),
  password: Yup.string().min(3).max(50).required('Password ujian wajib diisi'),
})

const isUseCaptcha = process.env.REACT_APP_USE_CAPTCHA === 'true'
const captchaKey = process.env.REACT_APP_CAPTCHA_TOKEN ?? ''

export function ExamLogin() {
  const {saveAuth, setCurrentUser} = useAuth()
  const [loading, setLoading] = useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string>('')
  const [refreshCaptcha, setRefreshCaptcha] = useState(false)
  const navigate = useNavigate()

  const onVerify = useCallback((token: string) => {
    setCaptchaToken(token)
  }, [])

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setRefreshCaptcha((r) => !r)
    }, intervalRefreshRecaptcha)
    return () => clearInterval(intervalId)
  }, [])

  const formik = useFormik({
    initialValues: {username: '', password: ''},
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        setLoading(true)
        setStatus(undefined)

        let authRequest = {captchaToken: '', ...values}
        if (isUseCaptcha) {
          authRequest = {captchaToken, ...values}
        }

        const response = await authExam(authRequest)
        const {token, userInfo} = response.data

        const currentUser = {
          username: userInfo.loginName,
          email: userInfo.additionalInfo.email,
          majorName: userInfo.additionalInfo.studentMajorName,
          degreeName: userInfo.additionalInfo.studentMajorDegreeName,
          nim: userInfo.additionalInfo.studentMajorNIM,
          id: userInfo.id,
          name: userInfo.name,
          roleId: userInfo.roles[0].id,
          roleName: userInfo.roles[0].name,
          limitFileSize: userInfo.additionalInfo.limitFileSize,
          limitFileSizeMb: userInfo.additionalInfo.limitFileSize / 1048576,
          staffMajors: userInfo.additionalInfo.staffMajorList
            ? parseJsonAndPascalToCamel(userInfo.additionalInfo.staffMajorList)
            : null,
        }

        setCurrentUser(currentUser)
        saveAuth({api_token: token, authMode: 'exam'})
        navigate('/exam-login')
      } catch (err: any) {
        setStatus(err?.message ?? 'Login ujian gagal')
      } finally {
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  const togglePasswordVisibility = () => setIsPasswordVisible((v) => !v)

  return (
    <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Login Ujian / Quiz</h1>
        <div className='text-gray-500 fw-semibold fs-6'>Gunakan password ujian dari proktor</div>
      </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>NIM</label>
        <input
          placeholder='NIM'
          {...formik.getFieldProps('username')}
          className={clsx('form-control bg-transparent', {
            'is-invalid': formik.touched.username && formik.errors.username,
          })}
          autoComplete='off'
        />
      </div>

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password ujian</label>
        <InputGroup className='mb-3'>
          <input
            type={isPasswordVisible ? 'text' : 'password'}
            placeholder='Password ujian'
            {...formik.getFieldProps('password')}
            className='form-control bg-transparent'
            autoComplete='off'
          />
          <InputGroup.Text role='button' onClick={togglePasswordVisibility}>
            <i className={!isPasswordVisible ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
          </InputGroup.Text>
        </InputGroup>
      </div>

      <div className='d-grid mb-3'>
        <LoadingButtonWrapper isLoading={loading}>
          <button type='submit' className='btn btn-danger w-100' disabled={formik.isSubmitting}>
            Masuk ujian
          </button>
        </LoadingButtonWrapper>
      </div>

      <div className='text-center'>
        <Link to='/auth/login' className='link-primary'>
          Login portal biasa
        </Link>
      </div>

      {isUseCaptcha && (
        <GoogleReCaptchaProvider reCaptchaKey={captchaKey}>
          <GoogleReCaptcha onVerify={onVerify} refreshReCaptcha={refreshCaptcha} action='login' />
        </GoogleReCaptchaProvider>
      )}
    </form>
  )
}
